import { Box, Button, Flex, HStack, Spinner, Tooltip } from '@chakra-ui/react';
import { IconBold, IconCode, IconFilePlus, IconItalic, IconList, IconListNumbers, IconPlayerPlayFilled, IconStrikethrough, IconUnderline, IconX } from '@tabler/icons-react';
import isHotkey from 'is-hotkey';
import { capitalize } from 'lodash';
import { memo, useCallback, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Editor, Path, Range, Element as SlateElement, Node as SlateNode, Transforms, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import uppyUtility from '../../../config/uppi';
import { ChatContext } from '../../../contexts/ChatContexts';
import { useSocket } from '../../Socket';
import { AddLinkButton, BlockButton, CodeBlockButton, MarkButton, RemoveLinkButton } from './Buttons';
import Element from './Element';
import Leaf from './Leaf';
import Toolbar from './Toolbar';
import style from './style.module.css';
import { HOTKEYS, LIST_TYPES, SHORTCUTS, SetNodeToDecorations, handleBackspace, handleShiftEnter, prismThemeCss, serialize, toggleMark, useDecorate, withInlines } from './ulility';

// TODO:
// [UX]: When users hits Shift + Enter 2 times, end current formatting block and move cursor to out of the block
// [UI] Prism css is not applying on message input and on message history view item


function deserialize(el) {
  if (el.nodeType === 3) {
    const text = el.textContent.replace(/\n/g, '');
    return text ? { text } : null;
  }

  if (el.nodeType !== 1) return null;

  // if (el.nodeName === 'BR') {
  //   return { text: '\n' };
  // }

  const nodeName = el.nodeName.toLowerCase();
  let children = Array.from(el.childNodes)
    .map(deserialize)
    .flat()
    .filter(child => child != null);

  if (children.length === 0) {
    children = [{ text: '' }];
  }

  switch (nodeName) {
    case 'body':
      return children;

    case 'p':
      return { type: 'paragraph', children };

    case 'strong':
      return children.map(child => ({ ...child, bold: true }));

    case 'em':
      return children.map(child => ({ ...child, italic: true }));

    case 'u':
      return children.map(child => ({ ...child, underline: true }));

    case 's':
    case 'del':
      return children.map(child => ({ ...child, strikethrough: true }));

    case 'li':
      return { type: 'list-item', children };

    case 'ul':
      return { type: 'bulleted-list', children };

    case 'ol':
      return { type: 'numbered-list', children };

    case 'code':
      return children.map(child => ({ ...child, code: true }));

    default:
      return children;
  }
}



const deserializeHTML = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return deserialize(doc.body);
};



function MessageInput({ files, openFileDialog, valueProps, messageId, onCancel }) {

  const initialValue = valueProps
    ? deserializeHTML(valueProps)
    : [{ type: 'paragraph', children: [{ text: '' }] }];

  const [chatState, , { editMessage }] = useContext(ChatContext);


  const { channelId, channelType } = useParams();
  const [editor] = useState(() => withInlines(withHistory(withReact(createEditor()))));
  // const [shiftEnterCount, setShiftEnterCount] = useState(0);
  const [value, setValue] = useState(initialValue);
  const { socket } = useSocket();
  const renderElement = useCallback(props => <Element {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  const resetEditor = useCallback(() => {
    editor.children = initialValue;
    editor.normalize({ force: true });
    Transforms.select(editor, Editor.start(editor, []));

    setValue([...initialValue]);

  }, [editor]);

  const onChange = useCallback(
    (newValue) => {
      setValue(newValue);
    },
    [editor]
  );

  const handleDOMBeforeInput = useCallback(
    (e) => {
      queueMicrotask(() => {
        const pendingDiffs = ReactEditor.androidPendingDiffs(editor);

        const scheduleFlush = pendingDiffs?.some(({ diff, path }) => {
          if (!diff.text.endsWith(' ')) {
            return false;
          }

          const { text } = SlateNode.leaf(editor, path);
          const beforeText = text.slice(0, diff.start) + diff.text.slice(0, -1);
          if (!(beforeText in SHORTCUTS)) {
            return;
          }

          const blockEntry = Editor.above(editor, {
            at: path,
            match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n),
          });
          if (!blockEntry) {
            return false;
          }

          const [, blockPath] = blockEntry;
          return Editor.isStart(editor, Editor.start(editor, path), blockPath);
        });

        if (scheduleFlush) {
          ReactEditor.androidScheduleFlush(editor);
        }
      });
    },
    [editor]
  );

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const { nativeEvent } = event;
      if (messageId) {
        handleShiftEnter(editor, nativeEvent);
      } else {
        handleSubmit(event);
      }
    } else if (event.key === "ArrowDown") {
      // deactivateCodeBlock(editor);
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const [node, path] = Editor.parent(editor, selection);
        if (
          SlateElement.isElement(node) &&
          (node.type === "code-line" || LIST_TYPES.includes(node.type))
        ) {
          const end = Editor.end(editor, path);
          // Check if we're at the end of the current block
          if (Editor.isEnd(editor, selection.anchor, end)) {
            event.preventDefault();
            const nextPath = Path.next(path);

            if (Editor.hasPath(editor, nextPath)) {
              // If there's a next block, move to its start
              Transforms.select(editor, Editor.start(editor, nextPath));
            } else {
              // If there's no next block, insert a new paragraph
              insertNewParagraph(path);
            }
          }
        }
      }
    }
    else if (event.key === "Backspace") {
      handleBackspace(editor);
    }

    function insertNewParagraph(path) {
      const parentPath = Path.parent(path);
      const nextPath = Path.next(parentPath);

      Transforms.insertNodes(
        editor,
        { type: "paragraph", children: [{ text: "" }] },
        { at: nextPath }
      );
      Transforms.select(editor, Editor.start(editor, nextPath));
    }

    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        toggleMark(editor, mark);
      }
    }
  };
  const renderFilePreview = () => {
    return (files.length > 0 &&
      <HStack m={2}>
        {
          files.map(fileObj =>
            <FilePreviewItem file={fileObj} key={fileObj.id} />
          )
        }
      </HStack>);
  };

  const containsOnlyBlankPTags = (html) => {
    const cleanHtml = html.replace(/\s+/g, '');

    const nonBlankPRegex = /<p[^>]*>(.*?)<\/p>/i;

    const nonBlankLiRegex = /<li[^>]*>(.*?)<\/li>/ig;

    const hasNonBlankP = nonBlankPRegex.test(cleanHtml) && cleanHtml.match(nonBlankPRegex)[1].trim() !== '';
    const hasNonBlankLi = nonBlankLiRegex.test(cleanHtml) && cleanHtml.match(nonBlankLiRegex).some(item => item.replace(/<\/?li[^>]*>/g, '').trim() !== '');

    return !(hasNonBlankP || hasNonBlankLi);
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    const html = serialize(editor.children);
    let messageType = "";

    const emptyValueRegEx = containsOnlyBlankPTags(html);

    if (emptyValueRegEx && !files.length)
      return;

    if (files.length && !emptyValueRegEx) {
      messageType = "TEXT_MEDIA";
    } else if (files.length) {
      messageType = "MEDIA";
    } else {
      messageType = "TEXT";
    }

    const payload = {
      channelId,
      chatType: capitalize(channelType) + 's',
      text: emptyValueRegEx ? "" : html,
      // text: html,
      type: messageType,
      fileURL: files?.map((file) => file.uploadURL),
    };

    socket.emit('MESSAGE_SEND', payload);
    resetEditor();
    uppyUtility.cancelAll();
  };

  const decorate = useDecorate(editor);

  const resetFormatting = useCallback(() => {
    Transforms.setNodes(
      editor,
      { type: 'paragraph' },
      { match: (n) => Editor.isBlock(editor, n) }
    );
    Transforms.unsetNodes(editor, Object.keys(Text.decorations), { match: Text.isText });
  }, [editor]);
  // useEffect(() => {
  //   console.log(plainText, '>>>>>> plainText');
  //   initialValue = [
  //     {
  //       type: 'paragraph',
  //       children: [{
  //         text: plainText === undefined ? "" : plainText,
  //       }]
  //     }
  //   ];
  // }, [valueProps]);

  const handleSave = async () => {
    try {
      const updatedText = serialize(value);
      await editMessage(messageId, updatedText);
      console.log("Sending updated message:", updatedText);
      if (onCancel) onCancel();
    } catch (err) {
      console.error("Error editing message:", err);
    }
  };



  return (
    <form onSubmit={handleSubmit}>
      <Box className='slate-editor slate-editor-wrapper' border={'1px solid'} borderRadius={'10px'} borderColor={'gray.300'} boxShadow={' 0 -2px 10px 4px white'} pos={'relative'} zIndex={1} maxH='calc(100vh-80%)' >
        <Slate editor={editor} initialValue={initialValue} value={value} onChange={onChange}>
          <Toolbar>
            <Tooltip label="Bold" hasArrow placement='top'>
              <span><MarkButton type={value?.[value?.length - 1]?.type} format="bold" icon={IconBold} /></span>
            </Tooltip>
            <Tooltip label="Italic" hasArrow placement='top'>
              <span><MarkButton type={value?.[value?.length - 1]?.type} format="italic" icon={IconItalic} /></span>
            </Tooltip>
            <Tooltip label="Underline" hasArrow placement='top'>
              <span>
                <MarkButton type={value?.[value?.length - 1]?.type} format="underline" icon={IconUnderline} />
              </span>
            </Tooltip>
            <Tooltip label="Strikethrough" hasArrow placement='top'>
              <span><MarkButton type={value?.[value?.length - 1]?.type} format="strike" icon={IconStrikethrough} /></span>
            </Tooltip>
            <Tooltip label="Code" hasArrow placement='top'>
              <span><MarkButton type={value?.[value?.length - 1]?.type} format="code" icon={IconCode} /></span>
            </Tooltip>
            <Tooltip label="Code block" hasArrow placement='top'>
              <span><CodeBlockButton editors={editor} type={value?.[value?.length - 1]?.type} format="code-block" /></span>
            </Tooltip>
            <Tooltip label="Link" hasArrow placement='top'>
              <span><AddLinkButton type={value?.[value?.length - 1]?.type} /></span>
            </Tooltip>
            <Tooltip label="Remove link" hasArrow placement='top'>
              <span><RemoveLinkButton type={value?.[value?.length - 1]?.type} /></span>
            </Tooltip>
            <Tooltip label="Ordered list" hasArrow placement='top'>
              <span>
            <BlockButton editors={editor} type={value?.[value?.length - 1]?.type} format="numbered-list" icon={IconListNumbers} />
              </span>
            </Tooltip>
            <Tooltip label="Bullet-point list" hasArrow placement='top'>
              <span>
            <BlockButton editors={editor} type={value?.[value?.length - 1]?.type} format="bulleted-list" icon={IconList} />
              </span>
            </Tooltip>
            <Tooltip label="Attach" hasArrow placement='top'>
              <span>
                {!messageId && (
                  <Button
              onClick={openFileDialog}
              size={'sm'}
              variant={"unstyled"}
              border={'1px solid'}
              borderColor={'gray.200'}
              p={1}>
                    <Box
                      display={"flex"}
                      justifyContent={'center'}
                      alignItems={'center'}
                    >
                      <IconFilePlus stroke={2} size={18} />
                    </Box>
                  </Button>
                )}
              </span>
            </Tooltip>
            {/* 
              TODO: Enable insert/delete Links
            */}
            {/* 
              TODO: Add File upload capabilities using Uppy
            */}
            {/* 
              TODO:  Add Audio/Video/Screen recording capabilities
             */}
            {/* TODO: Integrate Add link & remove link button */}
            {/* <AddLinkButton />
            <RemoveLinkButton /> */}
          </Toolbar>
          <SetNodeToDecorations />
          <Editable
            decorate={decorate}
            className={style.inputText}
            placeholder='Type something or Drop something here to send'
            onDOMBeforeInput={handleDOMBeforeInput}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            spellCheck
            renderPlaceholder={({ children, attributes }) => (
              <Box {...attributes} mt={'10px'}>
                <p>{children}</p>
              </Box>
            )
            }
            onKeyDown={onKeyDown}
          />
          <style>{prismThemeCss}</style>
        </Slate>

        {renderFilePreview()}

        {messageId && (
          <Flex justify={'end'} gap={'2'} padding={'2'}>
          <Button size="sm" variant="outline" onClick={() => onCancel()}>Cancel</Button>
            <Button size="sm" variant="outline" onClick={handleSave}>Save</Button>
        </Flex>
        )}
      </Box>
    </form>
  );
}

export default memo(MessageInput);

const FilePreviewItem = ({ file }) => {
  const removeFile = () => {
    uppyUtility.removeFile(file.id);
  };
  return (
    <Box
      key={file.id}
      height={'4rem'}
      width={'4rem'}
      bg={`url(${file.preview || file?.uploadURL})`}
      bgSize={'cover'}
      position={'relative'}
      borderRadius={'10px'}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      border={'1px solid'}
      borderColor={'gray.200'}
      cursor={'pointer'}
    >
      {
        !file?.progress?.uploadComplete &&
        <Spinner />
      }
      {
        file?.progress?.uploadComplete && file?.meta?.type.startsWith('video/') &&
        <IconPlayerPlayFilled color="white" />
      }
      <Button
        variant={'unstyled'}
        color={'white'}
        bg="blackAlpha.600"
        p={1}
        minW="20px"
        w="20px"
        minH="20px"
        h="20px"
        borderRadius={'100%'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        pos={'absolute'}
        right={'0'}
        top={'0'}
        transform={'translate(50%, -50%)'}
        onClick={removeFile}
        _hover={{ bg: 'blackAlpha.700' }}
      >
        <IconX stroke={4} size={18} />
      </Button>
    </Box>
  );
};